import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useAuthStore } from '@/stores/authStore'
import { profileService } from '@/services/profileService'
import { authService } from '@/services/authService'
import { contentApi } from '@/lib/api'
import {
  Settings,
  User,
  Shield,
  BarChart3,
  Camera,
  Eye,
  EyeOff,
  Check,
  X,
  Save,
  Trash2,
  AlertTriangle,
  Crown,
  Zap,
  CalendarDays,
  CalendarRange,
  Calendar,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { cn } from '@/utils/cn'

// Reutilizar lógica de força de senha do RegisterScreen
function getPasswordStrength(password: string) {
  const checks = {
    minLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  }
  const passed = Object.values(checks).filter(Boolean).length
  let level: 'none' | 'weak' | 'medium' | 'strong' = 'none'
  if (password.length === 0) level = 'none'
  else if (passed <= 2) level = 'weak'
  else if (passed <= 4) level = 'medium'
  else level = 'strong'
  return { checks, passed, level }
}

export function SettingsScreen() {
  const { user, updateProfile, logout } = useAuthStore()
  const navigate = useNavigate()

  // Profile Settings - Campos não editáveis (somente leitura)
  const [cpf, setCpf] = useState('')
  const [username, setUsername] = useState('')

  // Profile Settings - Campos editáveis básicos
  const [email, setEmail] = useState('')

  const [name, setName] = useState('')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [phone, setPhone] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [profession, setProfession] = useState('')

  // Questionário
  const [profileType, setProfileType] = useState('')
  const [howKnew, setHowKnew] = useState('')
  const [aiFamiliarity, setAiFamiliarity] = useState('')

  // Dados específicos por perfil - Produtor
  const [propertySizeHa, setPropertySizeHa] = useState('')
  const [biome, setBiome] = useState('')
  const [soilType, setSoilType] = useState('')
  const [propertyCity, setPropertyCity] = useState('')
  const [propertyState, setPropertyState] = useState('')
  const [productionSystem, setProductionSystem] = useState('')
  const [gaasAssociated, setGaasAssociated] = useState('')
  const [activities, setActivities] = useState<string[]>([])
  const [practices, setPractices] = useState<string[]>([])
  const [challenges, setChallenges] = useState<string[]>([])
  const [mainObjective, setMainObjective] = useState('')

  // Dados específicos - Profissional
  const [professionalFormation, setProfessionalFormation] = useState('')
  const [workArea, setWorkArea] = useState('')
  const [workRegion, setWorkRegion] = useState('')
  const [workType, setWorkType] = useState('')
  const [regenerativeExperience, setRegenerativeExperience] = useState('')
  const [experienceYears, setExperienceYears] = useState('')

  // Dados específicos - Acadêmico
  const [institution, setInstitution] = useState('')
  const [workField, setWorkField] = useState('')
  const [mainInterest, setMainInterest] = useState('')

  // Preferências
  const [preferredTopics, setPreferredTopics] = useState<string[]>([])
  const [observations, setObservations] = useState('')

  // Password Settings
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Delete Account Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [showDeletePassword, setShowDeletePassword] = useState(false)
  const [deletingAccount, setDeletingAccount] = useState(false)

  // UI State
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  // Usage data
  const [usageData, setUsageData] = useState<{
    plan: { name: string; slug: string } | null
    percentages: { daily: number; weekly: number; monthly: number }
    resetDates?: {
      daily: string
      weekly: string
      monthly: string
    }
  } | null>(null)
  const [loadingUsage, setLoadingUsage] = useState(false)

  const fetchUsage = async () => {
    setLoadingUsage(true)
    try {
      const { api } = await import('@/lib/api')
      const response = await api.get('/auth/usage')
      setUsageData(response.data)
    } catch (error) {
      console.error('Erro ao carregar uso:', error)
    } finally {
      setLoadingUsage(false)
    }
  }

  const passwordStrength = getPasswordStrength(newPassword)

  // Carregar dados do usuário do banco
  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        setLoadingProfile(true)
        try {
          // Buscar dados atualizados do banco
          const { user: profileData } = await profileService.getProfile() as { user: any }

          // Campos não editáveis (somente leitura)
          setEmail(profileData.email || user.email || '')
          setCpf(profileData.cpf || '')
          setUsername(profileData.username || '')

          // Dados básicos
          setName(profileData.name || user.name || '')
          setBio(profileData.bio || '')
          setAvatarUrl(profileData.avatarUrl || user.avatarUrl || '')
          setPhone(profileData.phone || '')
          setCity(profileData.city || '')
          setState(profileData.state || '')
          setProfession(profileData.profession || '')

          // Questionário
          setProfileType(profileData.profileType || '')
          setHowKnew(profileData.howKnew || '')
          setAiFamiliarity(profileData.aiFamiliarity || '')

          // Dados específicos - Produtor
          setPropertySizeHa(profileData.propertySizeHa || '')
          setBiome(profileData.biome || '')
          setSoilType(profileData.soilType || '')
          setPropertyCity(profileData.propertyCity || '')
          setPropertyState(profileData.propertyState || '')
          setProductionSystem(profileData.productionSystem || '')
          setGaasAssociated(profileData.gaasAssociated || '')
          setActivities(profileData.activities || [])
          setPractices(profileData.practices || [])
          setChallenges(profileData.challenges || [])
          setMainObjective(profileData.mainObjective || '')

          // Dados específicos - Profissional
          setProfessionalFormation(profileData.professionalFormation || '')
          setWorkArea(profileData.workArea || '')
          setWorkRegion(profileData.workRegion || '')
          setWorkType(profileData.workType || '')
          setRegenerativeExperience(profileData.regenerativeExperience || '')
          setExperienceYears(profileData.experienceYears || '')

          // Dados específicos - Acadêmico
          setInstitution(profileData.institution || '')
          setWorkField(profileData.workField || '')
          setMainInterest(profileData.mainInterest || '')

          // Preferências
          setPreferredTopics(profileData.preferredTopics || [])
          setObservations(profileData.observations || '')

        } catch (error) {
          console.error('Erro ao carregar perfil:', error)
          // Fallback para dados do AuthStore
          setEmail(user.email || '')
          setName(user.name || '')
          setUsername(user.username || '')
          setBio(user.bio || '')
          setAvatarUrl(user.avatarUrl || '')
        } finally {
          setLoadingProfile(false)
        }
      }
    }

    loadProfile()
  }, [user])


  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => {
      setMessage(null)
    }, 5000) // Aumentado para 5 segundos
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'Arquivo muito grande. Máximo 5MB.')
      return
    }

    if (!file.type.match(/image\/(jpeg|png|webp|gif)/)) {
      showMessage('error', 'Formato inválido. Use JPEG, PNG, WebP ou GIF.')
      return
    }

    setUploadingAvatar(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await contentApi.post('/content/attachments/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setAvatarUrl(response.data.file_url)
      showMessage('success', 'Foto de perfil atualizada! Salve o perfil para confirmar.')
    } catch (error: any) {
      showMessage('error', error?.response?.data?.error || 'Erro ao fazer upload da foto.')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Preparar dados para envio
      const profileData = {
        // Dados básicos
        email,
        bio,
        avatarUrl,
        phone,
        city,
        state,
        profession,

        // Questionário
        profileType,
        howKnew,
        aiFamiliarity,

        // Dados específicos - Produtor
        propertySizeHa: propertySizeHa ? parseFloat(propertySizeHa) : undefined,
        biome,
        soilType,
        propertyCity,
        propertyState,
        productionSystem,
        gaasAssociated,
        activities,
        practices,
        challenges,
        mainObjective,

        // Dados específicos - Profissional
        professionalFormation,
        workArea,
        workRegion,
        workType,
        regenerativeExperience,
        experienceYears,

        // Dados específicos - Acadêmico
        institution,
        workField,
        mainInterest,

        // Preferências
        preferredTopics,
        observations,
      }

      // Filtrar e validar campos antes de enviar
      const filteredData = Object.fromEntries(
        Object.entries(profileData).filter(([key, value]) => {
          // Remover undefined, strings vazias e arrays vazios
          if (value === undefined || value === '') return false
          if (Array.isArray(value) && value.length === 0) return false

          // Validar campos de estado (devem ter exatamente 2 caracteres)
          if ((key === 'state' || key === 'propertyState') && typeof value === 'string') {
            return value.length === 2
          }

          return true
        })
      )

      // Usar updateProfile do AuthStore
      await updateProfile(filteredData as any)
      showMessage('success', 'Perfil atualizado com sucesso!')

      // Scroll para o topo para garantir que a mensagem seja visível
      window.scrollTo({ top: 0, behavior: 'smooth' })

    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.details?.join(', ') || error.message || 'Erro ao atualizar perfil.'
      showMessage('error', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validar senha atual
      if (!currentPassword) {
        showMessage('error', 'Digite sua senha atual.')
        setLoading(false)
        return
      }

      // Validar nova senha
      if (passwordStrength.level !== 'strong') {
        showMessage('error', 'Nova senha não atende todos os requisitos.')
        setLoading(false)
        return
      }

      if (newPassword !== confirmPassword) {
        showMessage('error', 'As senhas não coincidem.')
        setLoading(false)
        return
      }

      // Chamar API real
      await authService.changePassword(currentPassword, newPassword)

      showMessage('success', 'Senha alterada com sucesso!')

      // Limpar campos
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')

      // Scroll para o topo para garantir que a mensagem seja visível
      window.scrollTo({ top: 0, behavior: 'smooth' })

    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao alterar senha.'
      showMessage('error', errorMessage)
      // Scroll para o topo para garantir que a mensagem de erro seja visível
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      showMessage('error', 'Digite sua senha para confirmar a exclusão.')
      return
    }
    setDeletingAccount(true)
    try {
      await authService.deleteAccount(deletePassword)
      logout()
      navigate('/login', { replace: true, state: { accountDeleted: true } })
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao excluir conta.'
      showMessage('error', errorMessage)
      setShowDeleteModal(false)
      setDeletePassword('')
    } finally {
      setDeletingAccount(false)
    }
  }

  const tabs = [
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'security', label: 'Segurança', icon: Shield },
    { id: 'plan', label: 'Meu Plano', icon: BarChart3 },
  ]

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <div className="bg-brand-surface border-b border-brand-border">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Breadcrumb
            items={[
              { label: 'Chat', href: '/chat' },
              { label: 'Configurações' }
            ]}
            className="mb-3"
          />
          <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
            <Settings size={24} />
            Configurações
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-24 sm:pb-8">
        {/* Loading Profile */}
        {loadingProfile && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-brand-text-secondary">Carregando perfil...</span>
          </div>
        )}

        {/* Tabs */}
        {!loadingProfile && (
          <div className="flex gap-2 mb-8 bg-brand-surface-alt rounded-xl p-1">
            <div className="flex gap-2 w-full overflow-x-auto justify-center sm:justify-start">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5 rounded-lg font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 text-sm sm:gap-3 sm:px-5 sm:text-base",
                    activeTab === id
                      ? "bg-brand-surface text-brand-primary shadow-sm"
                      : "text-brand-text-secondary hover:text-brand-text"
                  )}
                >
                  <Icon size={18} className="sm:size-20" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message - Fixed at top */}
        {message && (
          <div className={cn(
            "fixed top-4 left-1/2 -translate-x-1/2 z-50 p-4 rounded-xl flex items-center gap-3 shadow-lg animate-in slide-in-from-top-5 duration-300 max-w-md w-full mx-4",
            message.type === 'success'
              ? "bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-100 border-2 border-green-500"
              : "bg-red-50 dark:bg-red-900 text-red-700 dark:text-red-100 border-2 border-red-500"
          )}>
            {message.type === 'success' ? <Check size={20} className="flex-shrink-0" /> : <X size={20} className="flex-shrink-0" />}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* Tab Content */}
        {!loadingProfile && activeTab === 'profile' && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-brand-text mb-6">Perfil do Usuário</h2>

            <form onSubmit={handleProfileSave} className="space-y-6">
              {/* Avatar */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-3">
                  Foto de Perfil
                </label>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-24 h-24 bg-brand-accent rounded-full flex items-center justify-center overflow-hidden">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="Avatar"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-verde-500 text-2xl font-bold">
                          {name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <label
                      htmlFor="avatar-upload"
                      className={`absolute bottom-0 right-0 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-brand-primary-hover transition-colors ${uploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadingAvatar ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Camera size={16} className="text-white" />
                      )}
                    </label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleAvatarChange}
                      disabled={uploadingAvatar}
                      className="hidden"
                    />
                  </div>

                  <div className="flex-1">
                    <p className="text-sm text-brand-text-secondary mb-2">
                      Upload uma foto de perfil (JPEG, PNG, WebP ou GIF, máx. 5MB)
                    </p>
                    <p className="text-xs text-brand-text-muted">
                      {uploadingAvatar ? 'Enviando...' : 'A foto é salva automaticamente após o upload.'}
                    </p>
                  </div>
                </div>
              </div>


              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="seu@email.com"
                />
              </div>

              {/* Campos não editáveis */}
              <div className="bg-brand-surface-alt p-4 rounded-lg space-y-4">
                <h3 className="text-sm font-semibold text-brand-text mb-3">Informações Fixas (não editáveis)</h3>

                {/* Nome */}
                {name && (
                  <div>
                    <label className="block text-sm font-medium text-brand-text-secondary mb-2">
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      value={name}
                      disabled
                      className="input-field bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed opacity-60"
                    />
                  </div>
                )}

                {/* CPF */}
                {cpf && (
                  <div>
                    <label className="block text-sm font-medium text-brand-text-secondary mb-2">
                      CPF
                    </label>
                    <input
                      type="text"
                      value={cpf}
                      disabled
                      className="input-field bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed opacity-60"
                    />
                  </div>
                )}

                {/* Username */}
                {username && (
                  <div>
                    <label className="block text-sm font-medium text-brand-text-secondary mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={username}
                      disabled
                      className="input-field bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed opacity-60"
                    />
                  </div>
                )}
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Bio
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="input-field resize-none"
                  rows={4}
                  placeholder="Fale um pouco sobre você..."
                  maxLength={500}
                />
                <p className="text-xs text-brand-text-muted mt-1">
                  {bio.length}/500 caracteres
                </p>
              </div>

              {/* Dados de Contato */}
              <div className="border-t border-brand-border pt-6">
                <h3 className="text-lg font-semibold text-brand-text mb-4">Dados de Contato</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="input-field"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Profissão
                    </label>
                    <input
                      type="text"
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      className="input-field"
                      placeholder="Sua profissão"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="input-field"
                      placeholder="Sua cidade"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Estado
                    </label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="input-field"
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>

              {/* Questionário */}
              <div className="border-t border-brand-border pt-6">
                <h3 className="text-lg font-semibold text-brand-text mb-4">Informações do Questionário</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Tipo de Perfil
                    </label>
                    <select
                      value={profileType}
                      onChange={(e) => setProfileType(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Selecione...</option>
                      <option value="producer">Produtor Rural</option>
                      <option value="professional">Profissional da Área</option>
                      <option value="academic">Acadêmico/Pesquisador</option>
                      <option value="curious">Curioso/Interessado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Como conheceu a RegenerAI?
                    </label>
                    <input
                      type="text"
                      value={howKnew}
                      onChange={(e) => setHowKnew(e.target.value)}
                      className="input-field"
                      placeholder="Ex: Redes sociais, indicação, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Familiaridade com IA
                    </label>
                    <select
                      value={aiFamiliarity}
                      onChange={(e) => setAiFamiliarity(e.target.value)}
                      className="input-field"
                    >
                      <option value="">Selecione...</option>
                      <option value="nenhuma">Nenhuma</option>
                      <option value="basica">Básica</option>
                      <option value="intermediaria">Intermediária</option>
                      <option value="avancada">Avançada</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Dados Específicos por Tipo de Perfil */}
              {profileType === 'producer' && (
                <div className="border-t border-brand-border pt-6">
                  <h3 className="text-lg font-semibold text-brand-text mb-4">🌾 Dados da Propriedade</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Tamanho da Propriedade (ha)
                      </label>
                      <input
                        type="number"
                        value={propertySizeHa}
                        onChange={(e) => setPropertySizeHa(e.target.value)}
                        className="input-field"
                        placeholder="Ex: 50"
                        min="0"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Bioma
                      </label>
                      <select
                        value={biome}
                        onChange={(e) => setBiome(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Amazônia">Amazônia</option>
                        <option value="Cerrado">Cerrado</option>
                        <option value="Caatinga">Caatinga</option>
                        <option value="Mata Atlântica">Mata Atlântica</option>
                        <option value="Pampa">Pampa</option>
                        <option value="Pantanal">Pantanal</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Tipo de Solo
                      </label>
                      <input
                        type="text"
                        value={soilType}
                        onChange={(e) => setSoilType(e.target.value)}
                        className="input-field"
                        placeholder="Ex: Latossolo, Argissolo..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Sistema de Produção
                      </label>
                      <select
                        value={productionSystem}
                        onChange={(e) => setProductionSystem(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Convencional">Convencional</option>
                        <option value="Em transição">Em transição</option>
                        <option value="Agroecológico">Agroecológico</option>
                        <option value="Orgânico">Orgânico</option>
                        <option value="Regenerativo">Regenerativo</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Cidade da Propriedade
                      </label>
                      <input
                        type="text"
                        value={propertyCity}
                        onChange={(e) => setPropertyCity(e.target.value)}
                        className="input-field"
                        placeholder="Cidade"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Estado da Propriedade
                      </label>
                      <input
                        type="text"
                        value={propertyState}
                        onChange={(e) => setPropertyState(e.target.value)}
                        className="input-field"
                        placeholder="UF"
                        maxLength={2}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Associado ao GAAS?
                      </label>
                      <select
                        value={gaasAssociated}
                        onChange={(e) => setGaasAssociated(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Sim">Sim</option>
                        <option value="Não">Não</option>
                        <option value="Em processo">Em processo</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Objetivo Principal
                      </label>
                      <textarea
                        value={mainObjective}
                        onChange={(e) => setMainObjective(e.target.value)}
                        className="input-field resize-none"
                        rows={3}
                        placeholder="Descreva seu objetivo principal com a agricultura regenerativa..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {profileType === 'professional' && (
                <div className="border-t border-brand-border pt-6">
                  <h3 className="text-lg font-semibold text-brand-text mb-4">💼 Dados Profissionais</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Formação Profissional
                      </label>
                      <input
                        type="text"
                        value={professionalFormation}
                        onChange={(e) => setProfessionalFormation(e.target.value)}
                        className="input-field"
                        placeholder="Ex: Agronomia, Engenharia Ambiental..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Área de Atuação
                      </label>
                      <input
                        type="text"
                        value={workArea}
                        onChange={(e) => setWorkArea(e.target.value)}
                        className="input-field"
                        placeholder="Ex: Consultoria, Assistência Técnica..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Região de Atuação
                      </label>
                      <input
                        type="text"
                        value={workRegion}
                        onChange={(e) => setWorkRegion(e.target.value)}
                        className="input-field"
                        placeholder="Ex: Sudeste, Nacional..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Tipo de Trabalho
                      </label>
                      <select
                        value={workType}
                        onChange={(e) => setWorkType(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Autônomo">Autônomo</option>
                        <option value="Empresa">Empresa</option>
                        <option value="Cooperativa">Cooperativa</option>
                        <option value="ONG">ONG</option>
                        <option value="Governo">Governo</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Experiência com Agricultura Regenerativa
                      </label>
                      <select
                        value={regenerativeExperience}
                        onChange={(e) => setRegenerativeExperience(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Nenhuma">Nenhuma</option>
                        <option value="Básica">Básica</option>
                        <option value="Intermediária">Intermediária</option>
                        <option value="Avançada">Avançada</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Anos de Experiência
                      </label>
                      <select
                        value={experienceYears}
                        onChange={(e) => setExperienceYears(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Menos de 1 ano">Menos de 1 ano</option>
                        <option value="1-3 anos">1-3 anos</option>
                        <option value="3-5 anos">3-5 anos</option>
                        <option value="5-10 anos">5-10 anos</option>
                        <option value="Mais de 10 anos">Mais de 10 anos</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {profileType === 'academic' && (
                <div className="border-t border-brand-border pt-6">
                  <h3 className="text-lg font-semibold text-brand-text mb-4">🎓 Dados Acadêmicos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Instituição
                      </label>
                      <input
                        type="text"
                        value={institution}
                        onChange={(e) => setInstitution(e.target.value)}
                        className="input-field"
                        placeholder="Nome da universidade/instituição"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Campo de Trabalho/Estudo
                      </label>
                      <select
                        value={workField}
                        onChange={(e) => setWorkField(e.target.value)}
                        className="input-field"
                      >
                        <option value="">Selecione...</option>
                        <option value="Graduação">Graduação</option>
                        <option value="Mestrado">Mestrado</option>
                        <option value="Doutorado">Doutorado</option>
                        <option value="Pós-doutorado">Pós-doutorado</option>
                        <option value="Pesquisa">Pesquisa</option>
                        <option value="Ensino">Ensino</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-brand-text mb-2">
                        Principal Interesse
                      </label>
                      <textarea
                        value={mainInterest}
                        onChange={(e) => setMainInterest(e.target.value)}
                        className="input-field resize-none"
                        rows={3}
                        placeholder="Descreva seu principal interesse em agricultura regenerativa..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Preferências */}
              <div className="border-t border-brand-border pt-6">
                <h3 className="text-lg font-semibold text-brand-text mb-4">Preferências</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Observações
                    </label>
                    <textarea
                      value={observations}
                      onChange={(e) => setObservations(e.target.value)}
                      className="input-field resize-none"
                      rows={3}
                      placeholder="Observações adicionais..."
                      maxLength={1000}
                    />
                    <p className="text-xs text-brand-text-muted mt-1">
                      {observations.length}/1000 caracteres
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={18} />
                {loading ? 'Salvando...' : 'Salvar Perfil'}
              </button>
            </form>
          </div>
        )}

        {!loadingProfile && activeTab === 'security' && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-brand-text mb-6">Segurança</h2>

            <form onSubmit={handlePasswordSave} className="space-y-6">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Senha Atual
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input-field pr-10"
                    placeholder="Digite sua senha atual"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={cn(
                      "input-field pr-10",
                      passwordStrength.level === 'weak' && "border-red-500 focus:ring-red-500",
                      passwordStrength.level === 'medium' && "border-yellow-500 focus:ring-yellow-500",
                      passwordStrength.level === 'strong' && "border-green-500 focus:ring-green-500"
                    )}
                    placeholder="Digite sua nova senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-brand-border rounded-full overflow-hidden">
                        <div
                          className={cn(
                            "h-full transition-all duration-300",
                            passwordStrength.level === 'weak' && "w-1/3 bg-red-500",
                            passwordStrength.level === 'medium' && "w-2/3 bg-yellow-500",
                            passwordStrength.level === 'strong' && "w-full bg-green-500"
                          )}
                        />
                      </div>
                      <span className={cn(
                        "text-xs font-medium",
                        passwordStrength.level === 'weak' && "text-red-500",
                        passwordStrength.level === 'medium' && "text-yellow-500",
                        passwordStrength.level === 'strong' && "text-green-500"
                      )}>
                        {passwordStrength.level === 'weak' && 'Fraca'}
                        {passwordStrength.level === 'medium' && 'Média'}
                        {passwordStrength.level === 'strong' && 'Forte'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={cn("flex items-center gap-1", passwordStrength.checks.minLength && "text-green-500")}>
                        {passwordStrength.checks.minLength ? <Check size={12} /> : <X size={12} />}
                        Mínimo 8 caracteres
                      </div>
                      <div className={cn("flex items-center gap-1", passwordStrength.checks.hasUpper && "text-green-500")}>
                        {passwordStrength.checks.hasUpper ? <Check size={12} /> : <X size={12} />}
                        Letra maiúscula
                      </div>
                      <div className={cn("flex items-center gap-1", passwordStrength.checks.hasLower && "text-green-500")}>
                        {passwordStrength.checks.hasLower ? <Check size={12} /> : <X size={12} />}
                        Letra minúscula
                      </div>
                      <div className={cn("flex items-center gap-1", passwordStrength.checks.hasNumber && "text-green-500")}>
                        {passwordStrength.checks.hasNumber ? <Check size={12} /> : <X size={12} />}
                        Número
                      </div>
                      <div className={cn("flex items-center gap-1", passwordStrength.checks.hasSpecial && "text-green-500")}>
                        {passwordStrength.checks.hasSpecial ? <Check size={12} /> : <X size={12} />}
                        Caractere especial
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={cn(
                      "input-field pr-10",
                      confirmPassword && confirmPassword !== newPassword && "border-red-500 focus:ring-red-500"
                    )}
                    placeholder="Confirme sua nova senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || passwordStrength.level !== 'strong' || confirmPassword !== newPassword}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Shield size={18} />
                {loading ? 'Alterando...' : 'Alterar Senha'}
              </button>
            </form>

            {/* Zona de Perigo */}
            <div className="mt-10 pt-8 border-t border-red-200 dark:border-red-900/40">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-red-500" />
                <h3 className="text-base font-semibold text-red-600 dark:text-red-400">Zona de Perigo</h3>
              </div>
              <p className="text-sm text-brand-text-secondary mb-4">
                A exclusão da conta é permanente e irreversível. Todos os seus dados pessoais serão apagados.
              </p>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-400 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium text-sm transition-colors"
              >
                <Trash2 size={16} />
                Excluir minha conta
              </button>
            </div>
          </div>
        )}

        {/* Modal de confirmação de exclusão */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
            <div className="bg-brand-surface rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-brand-text text-lg">Excluir conta</h3>
                  <p className="text-sm text-brand-text-secondary">Esta ação não pode ser desfeita.</p>
                </div>
              </div>

              <p className="text-sm text-brand-text-secondary">
                Todos os seus dados pessoais serão permanentemente apagados. Um hash criptográfico do seu CPF será retido para prevenir recadastro (LGPD Art. 16, II).
              </p>

              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">
                  Digite sua senha para confirmar
                </label>
                <div className="relative">
                  <input
                    type={showDeletePassword ? 'text' : 'password'}
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="input-field pr-10"
                    placeholder="Sua senha atual"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDeleteAccount() }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowDeletePassword(!showDeletePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
                  >
                    {showDeletePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowDeleteModal(false); setDeletePassword('') }}
                  disabled={deletingAccount}
                  className="flex-1 px-4 py-2 rounded-lg border border-brand-border text-brand-text-secondary hover:bg-brand-surface-alt font-medium text-sm transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={!deletePassword || deletingAccount}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Trash2 size={16} />
                  {deletingAccount ? 'Excluindo...' : 'Excluir conta'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!loadingProfile && activeTab === 'plan' && (
          <PlanTab
            usageData={usageData}
            loadingUsage={loadingUsage}
            fetchUsage={fetchUsage}
          />
        )}
      </div>
    </div>
  )
}

// ==========================================================================
// PlanTab — Meu Plano (uso real com porcentagens)
// ==========================================================================

const planStyles: Record<string, { gradient: string; badge: string; icon: typeof Zap }> = {
  free: { gradient: 'from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900', badge: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200', icon: Zap },
  tier1: { gradient: 'from-brand-accent/10 to-brand-primary-light', badge: 'bg-brand-primary/10 text-brand-primary', icon: Crown },
  tier2: { gradient: 'from-purple-100 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/20', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', icon: Crown },
  tier3: { gradient: 'from-amber-100 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/20', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Crown },
}

function UsageBar({ label, percent, icon: Icon, resetDate }: { label: string; percent: number; icon: typeof CalendarDays; resetDate?: string }) {
  const barColor =
    percent >= 90 ? 'bg-red-500' :
      percent >= 70 ? 'bg-amber-500' :
        'bg-brand-primary'

  const textColor =
    percent >= 90 ? 'text-red-600 dark:text-red-400' :
      percent >= 70 ? 'text-amber-600 dark:text-amber-400' :
        'text-brand-primary'

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-brand-text">
          <Icon size={16} className="text-brand-text-muted" />
          {label}
        </div>
        <span className={cn("text-sm font-bold", textColor)}>
          {percent}%
        </span>
      </div>
      <div className="h-3 bg-brand-surface-alt rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-500", barColor)}
          style={{ width: `${Math.max(percent, 1)}%` }}
        />
      </div>
      {resetDate && (
        <div className="flex items-center gap-1 text-xs text-brand-text-muted">
          <RefreshCw size={12} />
          <span>
            Reseta em <span className="font-medium text-brand-text">
              {new Date(resetDate).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })}
            </span>
          </span>
        </div>
      )}
      {!resetDate && (
        <p className="text-xs text-brand-text-muted">
          {percent >= 90
            ? 'Atenção: uso quase no limite'
            : percent >= 70
              ? 'Uso elevado neste período'
              : 'Uso dentro do esperado'}
        </p>
      )}
    </div>
  )
}

function PlanTab({
  usageData,
  loadingUsage,
  fetchUsage,
}: {
  usageData: {
    plan: { name: string; slug: string } | null
    percentages: { daily: number; weekly: number; monthly: number }
    resetDates?: {
      daily: string
      weekly: string
      monthly: string
    }
  } | null
  loadingUsage: boolean
  fetchUsage: () => Promise<void>
}) {
  useEffect(() => {
    fetchUsage()
  }, [])

  if (loadingUsage && !usageData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-primary" />
        <span className="ml-3 text-brand-text-secondary">Carregando dados do plano...</span>
      </div>
    )
  }

  const slug = usageData?.plan?.slug || 'free'
  const style = planStyles[slug] || planStyles.free
  const PlanIcon = style.icon
  const percentages = usageData?.percentages || { daily: 0, weekly: 0, monthly: 0 }

  return (
    <div className="space-y-6">
      {/* Plan Card */}
      <div className={cn("card p-6 bg-gradient-to-br", style.gradient)}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-text">Meu Plano</h2>
          <button
            onClick={fetchUsage}
            disabled={loadingUsage}
            className="text-sm text-brand-text-muted hover:text-brand-text transition-colors disabled:opacity-50"
          >
            {loadingUsage ? <Loader2 size={14} className="animate-spin" /> : 'Atualizar'}
          </button>
        </div>

        <div className="flex items-center gap-3 mb-2">
          <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", style.badge)}>
            <PlanIcon size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-brand-text">
              {usageData?.plan?.name || 'Gratuito'}
            </h3>
            <span className={cn("inline-block text-xs font-medium px-2 py-0.5 rounded-full", style.badge)}>
              {slug === 'free' ? 'Plano atual' : `Assinante ${usageData?.plan?.name}`}
            </span>
          </div>
        </div>
      </div>

      {/* Usage Section */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-brand-text mb-6">Uso do Plano</h3>

        <div className="space-y-6">
          <UsageBar label="Hoje" percent={percentages.daily} icon={CalendarDays} resetDate={usageData?.resetDates?.daily} />
          <UsageBar label="Esta semana" percent={percentages.weekly} icon={CalendarRange} resetDate={usageData?.resetDates?.weekly} />
          <UsageBar label="Este mês" percent={percentages.monthly} icon={Calendar} resetDate={usageData?.resetDates?.monthly} />
        </div>

        <div className="mt-6 p-3 bg-brand-surface-alt rounded-lg">
          <p className="text-xs text-brand-text-muted text-center">
            Os limites de uso são reiniciados automaticamente ao início de cada período.
          </p>
        </div>
      </div>
    </div>
  )
}
